import Immutable from 'immutable';
import * as diff from 'nike-ui/src/common/push/diff';
import * as LiveParser from '../api/liveParser';
import { deepMerge } from 'nike-ui/src/common/immutable/merge';

const MATCHES_TO_DISPLAY = 8;

function addMatchToCalendar(db, match) {
    const tourName = match.get('tournamentName');
    return db.updateIn(['upcoming', tourName], Immutable.List(), matches => matches.push(match));
}

function removeMatchFromCalendar(db, match) {
    const tourName = match.get('tournamentName');
    return db
        .updateIn(['upcoming', tourName], Immutable.List(), matches =>
            matches.filter(m => m.get('id') !== match.get('id'))
        )
        .update('upcoming', Immutable.Map(), tourMap => tourMap.filter(matches => !matches.isEmpty()));
}

function tourComparator(tour1, tour2) {
    if (tour1.get('priority') !== tour2.get('priority')) {
        return tour1.get('priority') - tour2.get('priority');
    }
    return tour1.get('label').localeCompare(tour2.get('label'));
}

function matchComparator(match1, match2) {
    if (!match2) {
        // debugger;
    }
    if (match1.get('start') !== match2.get('start')) {
        return match1.get('start') - match2.get('start');
    }
    if (match1.get('tournamentPriority') !== match2.get('tournamentPriority')) {
        return match1.get('tournamentPriority') - match2.get('tournamentPriority');
    }
    return match1.get('tournamentName').localeCompare(match2.get('tournamentName'));
}

function recalculateLiveIdsByTime(db) {
    const live = db.get('live', Immutable.Map());
    // ak je nastaveny filter by time tak niektore matche su neuplne lebo su vyfiltrovane ale chodia k nim
    // update spravy ktore sa ukladaju... tournamentName povazujeme za signal ze match je "uplny", zo snapshotu
    const matchesByTime = live
        .valueSeq()
        .map(m => m.get('match'))
        .filter(m => m && m.get('tournamentName'))
        .sort(matchComparator)
        .map(m =>
            Immutable.Map()
                .set('matchId', m.get('id'))
                .set('hasVideoStream', !!hasVideoStream(m))
                .set('boxId', m.get('boxId'))
        )
        .toList();
    return db.setIn(['liveIdsByTime'], matchesByTime);
}

function hasVideoStream(match) {
    return match.get('streams', Immutable.List()).find(s => s && s.get('streamType') === 'VIDEO');
}

export function clearData(db) {
    // clear boxIds filter
    return (
        db
            .delete('boxStore')
            // clear live match map
            .delete('liveTournaments')
            // clear matches count
            .delete('displayedMatchesCount')
            // clear upcoming match list
            .set('upcoming', Immutable.Map())
    );
}

export function processMatchDeleted(nikeOneDb, boxId, matchId) {
    let newDb = nikeOneDb.deleteIn(['live', matchId]);
    newDb = newDb.update('liveTournaments', liveTours => {
        const tourIndex = liveTours.findIndex(t => t.get('boxId') === boxId);
        if (tourIndex < 0) {
            return liveTours;
        }
        liveTours = liveTours.updateIn([tourIndex, 'matchIds'], ids => ids.filter(id => id !== matchId));
        if (liveTours.getIn([tourIndex, 'matchIds']).isEmpty()) {
            liveTours = liveTours.filter(t => t.get('boxId') !== boxId);
        }
        return liveTours;
    });
    return recalculateLiveIdsByTime(newDb);
}

function saveMatch(db, matchId, match) {
    return db.setIn(['live', matchId, 'match'], match);
}

function createTournamentForMatch(db, match) {
    return db.update('liveTournaments', Immutable.List(), liveTours => {
        const boxId = match.get('boxId');
        const hasVideo = hasVideoStream(match);
        const existingTourIndex = liveTours.findIndex(t => t.get('boxId') === boxId);
        if (existingTourIndex < 0) {
            return liveTours
                .push(
                    Immutable.fromJS({
                        boxId,
                        label: match.get('tournamentName'),
                        priority: match.get('tournamentPriority'),
                        matchIds: Immutable.List([match.get('id')]),
                        hasVideoStream: hasVideo
                    })
                )
                .sort(tourComparator);
        }

        return liveTours
            .updateIn([existingTourIndex, 'matchIds'], ids =>
                ids
                    .toSet()
                    .add(match.get('id'))
                    .toList()
            )
            .updateIn([existingTourIndex, 'hasVideoStream'], has => has || hasVideo);
    });
}

function createMatchLive(db, matchId, match) {
    if (!match.get('active')) {
        return addMatchToCalendar(db, match);
    }
    const newDb = createTournamentForMatch(db, match);
    return saveMatch(newDb, matchId, match);
}

export function processMatchCreated(db, matchId, delta, version) {
    const match = diff.applyDelta(delta, null, version);

    let newDb = createMatchLive(db, matchId, match);
    newDb = removeMatchFromCalendar(newDb, match);
    return recalculateLiveIdsByTime(newDb);
}

export function processMatchChanged(db, matchId, delta, version) {
    return db.updateIn(['live', matchId, 'match'], old => {
        // ignorujeme change spravu pokial nemame snapshot
        if (!old) {
            return old;
        }
        return LiveParser.addHalfTimeToPeriodScores(diff.applyDelta(delta, old, version));
    });
}

export function processBetCreatedOrChanged(db, matchId, betId, delta, version) {
    const newDb = db.updateIn(['live', matchId, 'bets', betId], old => diff.applyDelta(delta, old, version));
    return computeBetGroups(newDb, Immutable.List.of(matchId));
}

export function processBetDeleted(db, matchId, betId) {
    const newDb = db.deleteIn(['live', matchId, 'bets', betId]);
    return computeBetGroups(newDb, Immutable.List.of(matchId));
}

export function processBetsBatch(db, batch) {
    db = batch.reduce((locDb, { data, payloadEvent }) => {
        if (payloadEvent === 'DELETED') {
            return locDb.deleteIn(['live', data.matchId, 'bets', data.betId]);
        } else {
            return locDb.updateIn(['live', data.matchId, 'bets', data.betId], old =>
                diff.applyDelta(data.delta, old, data.version)
            );
        }
    }, db);
    return computeBetGroups(db, Immutable.Set(batch.map(item => item.data.matchId)));
}

export function processSnapshot(db, snapshot) {
    let newDb = db;
    newDb = snapshot.reduce((locDb, item) => {
        if (item.payload === 'MATCH') {
            const match = markItemVersion(item);
            return createMatchLive(locDb, item.matchId, match);
        }
        if (item.payload === 'BET') {
            return locDb.setIn(['live', item.matchId, 'bets', item.betId], markItemVersion(item));
        }
        if (item.payload === 'TOURNAMENT') {
            // zatial nepouzivame
            return locDb;
        }
        console.error('invalid payload in snapshot', item.payload, item);

        return locDb;
    }, newDb);

    newDb = recalculateLiveIdsByTime(newDb);
    return computeBetGroups(
        newDb,
        Immutable.Set(snapshot.filter(item => item.payload === 'BET').map(item => item.matchId))
    );
}

function markItemVersion(item) {
    const delta = Immutable.fromJS(item.delta);
    const versionsMap = delta.get('versionsMap');
    return diff.markVersion(delta.delete('versionsMap'), item.version).set('versionsMap', versionsMap);
}

export function openTournamentsNg(nikeOneDb) {
    let tournaments = nikeOneDb.get('liveTournaments', Immutable.List());
    const streamsOnlyView = nikeOneDb.getIn(['liveFilter', 'streamsOnly']);
    tournaments = tournaments.filter(t => (streamsOnlyView ? t.get('hasVideoStream') : true));
    if (tournaments.isEmpty()) {
        return nikeOneDb;
    }

    let matchesToDisplay = MATCHES_TO_DISPLAY;
    // prvy tournament vzdy rozbalime rozbaleny
    const firstBoxId = tournaments.getIn([0, 'boxId']);
    nikeOneDb = nikeOneDb.setIn(['liveOpenBoxes', firstBoxId], true);
    return tournaments.reduce((locDb, tour) => {
        matchesToDisplay -= tour.get('matchIds').size;
        if (matchesToDisplay <= 0) {
            return locDb;
        }
        // otvorime akordeon, ale ak je uz nastavene pouzivatelom false, tak nemenime
        return locDb.updateIn(['liveOpenBoxes', tour.get('boxId')], (t = null) => t !== false);
    }, nikeOneDb);
}

export function computeBetGroups(db, matchIds) {
    return matchIds.reduce((locDb, matchId) => {
        return locDb.updateIn(['live', matchId], Immutable.Map(), match => {
            const [groupIds, groups] = LiveParser.betGroups(match);
            return match
                .update('groups', Immutable.Map(), prev => deepMerge(prev, groups))
                .update('groupIds', Immutable.List(), prev => deepMerge(prev, groupIds));
        });
    }, db);
}
